import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, useParams } from "react-router-dom";

import { TrackingContext } from "context/Tracking";
import { BasePage } from "components/BasePage/BasePage";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { StartView } from "components/AIRiskAssessment/StartView";
import { HazardBoard } from "components/AIRiskAssessment/HazardBoard";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import { useAIRiskSession } from "components/AIRiskAssessment/hooks/useAIRiskSession";

interface AIRiskAssessmentRouteParams {
  projectId: string;
}

// Flag guard FIRST (plan §4): with the feature flag off this page is inert —
// deep links bounce straight back to the ordinary risks list. The guard lives
// in its own component so the data hooks below never run (and never change
// hook order) while the flag is off.
export const AIRiskAssessmentPage = () => {
  const { projectId } = useParams<AIRiskAssessmentRouteParams>();
  const { isFeatureEnabled } = useFeatureFlags();

  if (!isFeatureEnabled(Feature.AIRiskAssessment)) {
    return <Redirect to={`/projects/${projectId}/risks`} />;
  }

  return <AIRiskAssessmentContent projectId={projectId} />;
};

interface AIRiskAssessmentContentProps {
  projectId: string;
}

const AIRiskAssessmentContent = ({
  projectId,
}: AIRiskAssessmentContentProps) => {
  const { t } = useTranslation();
  const { track } = useContext(TrackingContext);
  const { project, fetchInProgress } = useProjectDetails(projectId);
  const { data: session, isLoading: sessionLoading } =
    useAIRiskSession(projectId);

  useEffect(() => {
    track("ai risk: open");
  }, [track]);

  return (
    <BasePage
      breadcrumbItems={[
        { label: "projects", location: "/dashboard" },
        {
          label: project?.projectdata.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: "steps.risks.label",
          location: `/projects/${projectId}/risks`,
        },
        {
          label: "risks.ai.breadcrumb",
          location: `/projects/${projectId}/risks/ai`,
        },
      ]}
      loading={fetchInProgress || sessionLoading}
    >
      <div>
        <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
          <h3 className="text-3xl leading-6 font-medium text-gray-900">
            {session
              ? t("risks.ai.board.headline")
              : t("risks.ai.start.headline")}
          </h3>
        </div>

        <p className="mt-4 text-sm text-gray-500">
          {session
            ? t("risks.ai.board.subline")
            : t("risks.ai.start.subline")}
        </p>

        {project &&
          (session ? (
            <HazardBoard
              projectId={projectId}
              project={project}
              session={session}
            />
          ) : (
            <StartView projectId={projectId} project={project} />
          ))}
      </div>
    </BasePage>
  );
};
